import { Component, lazy, ReactNode, Suspense } from 'react';

import { Button } from '@rikstv/play-common/src/components/button/Button';
import { captureExceptionInSentry } from '@rikstv/play-common/src/utils/errorTracker/tracking';

import { PlayerProps } from './Player';

const LazyPlayer = lazy(() =>
  import('./Player').then(module => ({
    default: module.Player,
  }))
);

export const LazyLoadedPlayer = (props: PlayerProps) => {
  return (
    <ErrorBoundary fallback={<PlayerLoadErrorFallback />}>
      <Suspense fallback={<span />}>
        <LazyPlayer {...props}></LazyPlayer>
      </Suspense>
    </ErrorBoundary>
  );
};

const PlayerLoadErrorFallback = () => (
  <div style={{ textAlign: 'center' }}>
    <p>Noe gikk galt med lastingen av avspillern...</p>
    <Button onClick={() => window.location.reload()}>Prøv igjen</Button>
  </div>
);

class ErrorBoundary extends Component<
  { fallback: NonNullable<ReactNode> | null; children?: ReactNode | undefined },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // error can actually be an event so default message to an empty string
    const { message = '' } = error;
    if (
      // Edge and Chrome
      message.includes('Failed to fetch dynamically imported module') ||
      // Firefox error
      message.includes('error loading dynamically imported module')
    ) {
      // 👆 means that app (probably) updated in background so a refresh should help
      if (shouldTryReloadToSolveError()) {
        window.location.reload();
        return;
      }
    }
    captureExceptionInSentry(error, { fingerprint: ['player-lazy-loading'] });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const shouldTryReloadToSolveError = () => {
  const key = 'player.lazy-load.error-refresh-timestamp';
  const lastFailedAttempt = JSON.parse(sessionStorage.getItem(key) || '0');
  if (Date.now() - lastFailedAttempt > 10_000) {
    // more than 10 seconds since last reload-attempt
    sessionStorage.setItem(key, Date.now().toString());
    return true;
  }
  return false;
};
